// ============================================================================
// Aides de formatage partagées par les pages Urgence / Remplaçants / Ateliers /
// Admin (client/pages/*). Locale fr-CH partout (CLAUDE.md §7 : UI 100%
// français). Pas d'appel réseau ici — pur formatage.
// ============================================================================
import type { StatutExterne, StatutFak } from '../../types';

export const JOURS_SEMAINE: Record<number, string> = {
  1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi', 5: 'Vendredi', 6: 'Samedi', 7: 'Dimanche',
};

const dateFmt = new Intl.DateTimeFormat('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
const dateLongFmt = new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
const dateCourtFmt = new Intl.DateTimeFormat('fr-CH', { weekday: 'short', day: 'numeric', month: 'short' });
const dateTimeFmt = new Intl.DateTimeFormat('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function asDate(iso: string): Date {
  // Les dates SQL (YYYY-MM-DD) sont interprétées en UTC par défaut par Date() ;
  // on force minuit local pour éviter un décalage d'un jour à l'affichage.
  return new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dateFmt.format(asDate(iso));
}

export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dateLongFmt.format(asDate(iso));
}

/** 'jeu. 28 août' — format compact pour un badge/une ligne dense. */
export function formatDateCourt(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dateCourtFmt.format(asDate(iso));
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dateTimeFmt.format(asDate(iso));
}

export function formatHoraire(debut: string | null | undefined, fin: string | null | undefined): string {
  if (!debut && !fin) return '—';
  const court = (h: string | null | undefined) => (h ? h.slice(0, 5) : '?');
  return `${court(debut)}–${court(fin)}`;
}

/** Aujourd'hui en date LOCALE « AAAA-MM-JJ » (jamais UTC : entre minuit et 1-2 h, la séance « d'aujourd'hui » est bien celle du jour). */
export function todayISO(): string {
  return dansNJoursISO(0);
}

export function demainISO(): string {
  return dansNJoursISO(1);
}

/** Date locale « AAAA-MM-JJ » à N jours d'aujourd'hui (même convention que todayISO/demainISO). */
export function dansNJoursISO(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Ajoute `mois` mois à une date ISO (par défaut : aujourd'hui). */
export function plusMois(mois: number, depuisISO?: string): string {
  const d = depuisISO ? asDate(depuisISO) : new Date();
  d.setMonth(d.getMonth() + mois);
  return d.toISOString().slice(0, 10);
}

/** Type d'urgence dérivé du jour de semaine de la date (weekend = samedi/dimanche). */
export function typeUrgenceAuto(dateISO: string): 'urgence_weekend' | 'urgence_semaine' {
  const jour = asDate(dateISO).getDay(); // 0=dimanche … 6=samedi
  return jour === 0 || jour === 6 ? 'urgence_weekend' : 'urgence_semaine';
}

/** Libellé « il y a X min » / « X h YY » depuis un horodatage ISO. */
export function depuis(iso: string, maintenant: number = Date.now()): string {
  const diffMs = maintenant - new Date(iso).getTime();
  const minutes = Math.max(0, Math.round(diffMs / 60000));
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h} h ${m.toString().padStart(2, '0')}`;
}

/** Classe CSS d'échéance (public/main.css) selon la proximité d'une date. */
export function echeanceClass(dateISO: string | null | undefined): string {
  if (!dateISO) return '';
  const d = asDate(dateISO);
  const today = asDate(todayISO());
  if (d < today) return 'echeance-ratee';
  const diffJours = (d.getTime() - today.getTime()) / 86400000;
  if (diffJours <= 14) return 'echeance-proche';
  return 'echeance-ok';
}

/** Libellé FR — sans ça, l'écran affiche la valeur brute ("visite_planifiee",
 *  "candidat") au lieu d'un texte lisible pour l'audience visée. */
export const LIBELLES_STATUT_EXTERNE: Record<StatutExterne, string> = {
  candidat: 'Candidat·e',
  visite_planifiee: 'Visite planifiée',
  actif: 'Actif·ve',
  inactif: 'Inactif·ve',
  archive: 'Archivé·e',
};

export function statutExterneBadgeClass(statut: StatutExterne | null | undefined): string {
  switch (statut) {
    case 'candidat': return 'badge badge-plain';
    case 'visite_planifiee': return 'badge badge-warn';
    case 'actif': return 'badge badge-ok';
    case 'inactif': return 'badge badge-err';
    case 'archive': return 'badge badge-err';
    default: return 'badge';
  }
}

export function fakBadgeClass(statut: StatutFak | null | undefined): string {
  switch (statut) {
    case 'manquant': return 'badge badge-err';
    case 'brouillon': return 'badge badge-warn';
    case 'publie': return 'badge badge-ok';
    case 'a_reviser': return 'badge badge-warn';
    case 'archive': return 'badge badge-plain';
    default: return 'badge badge-err';
  }
}
